import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { geographicOptions } from '@/pages/Onboarding/schema';
import type { Department, PositionTitle } from '@/types/oscar';
import { businessUnits, filterDepartments, type DepartmentRow } from './builderCatalogue';

/**
 * The department list the rule builder picks from, laid out like the Position
 * Title Allocator: filter by geography and business unit, search by code,
 * description or title, expand a department to see its titles. Ticking a
 * department puts its code in the rule (user.department); a title can also be
 * added on its own (user.jobTitle).
 */
export function DepartmentPicker({
  departments, titles, loading, failed, pickedDepartments, pickedTitles, onToggleDepartment, onAddDepartments, onAddTitle,
}: {
  departments: Department[];
  titles: PositionTitle[];
  loading: boolean;
  failed: boolean;
  pickedDepartments: string[];
  pickedTitles: string[];
  onToggleDepartment: (code: string) => void;
  onAddDepartments: (codes: string[]) => void;
  onAddTitle: (title: string) => void;
}) {
  const [geo, setGeo] = useState('');
  const [bu, setBu] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const rows = useMemo(() => filterDepartments(departments, titles, { geo, bu, q }), [departments, titles, geo, bu, q]);
  const units = useMemo(() => businessUnits(departments), [departments]);
  const picked = new Set(pickedDepartments);
  const pickedTitleSet = new Set(pickedTitles.map((t) => t.toLowerCase()));
  const unpickedShown = rows.map((r) => r.department.DepartmentCode).filter((c) => !picked.has(c));
  const filtered = geo !== '' || bu !== '' || q.trim() !== '';

  const toggleOpen = (code: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Geographic
          <select aria-label="Filter by geographic scope" value={geo} onChange={(e) => setGeo(e.target.value)}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {geographicOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Business unit
          <select aria-label="Filter by business unit" value={bu} onChange={(e) => setBu(e.target.value)}
            className="mt-1 max-w-[14rem] rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {units.map(([code, desc]) => <option key={code} value={code}>{desc} ({code})</option>)}
          </select>
        </label>
        <label className="flex min-w-[12rem] flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search departments
          <input aria-label="Search departments" type="search" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Code, description or position title"
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
      </div>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-darkGrey/70" aria-live="polite">
          {loading ? 'Loading departments…' : `${rows.length} ${rows.length === 1 ? 'department' : 'departments'}${filtered ? ' match' : ''}`}
        </span>
        <span className="flex gap-2">
          {filtered && unpickedShown.length > 0 && (
            <button type="button" onClick={() => onAddDepartments(unpickedShown)}
              className="rounded border border-navy bg-white px-2 py-1 font-semibold text-navy hover:bg-navy/5">
              Add all {unpickedShown.length} shown
            </button>
          )}
          <button type="button" onClick={() => setOpen(new Set(rows.filter((r) => r.titleHits.size === 0).map((r) => r.department.DepartmentCode)))}
            className="rounded border border-border bg-white px-2 py-1 font-medium text-darkGrey hover:bg-bg">Expand all</button>
          <button type="button" onClick={() => setOpen(new Set(rows.filter((r) => r.titleHits.size > 0).map((r) => r.department.DepartmentCode)))}
            className="rounded border border-border bg-white px-2 py-1 font-medium text-darkGrey hover:bg-bg">Collapse all</button>
        </span>
      </div>

      {failed && <p className="text-sm text-red" role="alert">Departments or position titles could not be loaded. Reload the page to try again.</p>}
      {!loading && !failed && rows.length === 0 && <p className="text-sm text-darkGrey/70">No department matches.</p>}

      <ul className="max-h-[32rem] space-y-1.5 overflow-y-auto pr-1" aria-label="Departments">
        {rows.map((row) => (
          <DepartmentItem key={row.department.DepartmentCode} row={row}
            picked={picked.has(row.department.DepartmentCode)}
            // A title hit opens the row by default; the chevron then flips it closed.
            expanded={open.has(row.department.DepartmentCode) !== (row.titleHits.size > 0)}
            pickedTitles={pickedTitleSet}
            onToggle={() => onToggleDepartment(row.department.DepartmentCode)}
            onExpand={() => toggleOpen(row.department.DepartmentCode)}
            onAddTitle={onAddTitle} />
        ))}
      </ul>
    </div>
  );
}

function DepartmentItem({ row, picked, expanded, pickedTitles, onToggle, onExpand, onAddTitle }: {
  row: DepartmentRow;
  picked: boolean;
  expanded: boolean;
  pickedTitles: Set<string>;
  onToggle: () => void;
  onExpand: () => void;
  onAddTitle: (title: string) => void;
}) {
  const d = row.department;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <li className={cn('rounded border bg-white shadow-sm', picked ? 'border-navy ring-1 ring-navy/30' : 'border-border')}>
      <div className="flex items-center gap-2 px-3 py-2">
        <input type="checkbox" checked={picked} onChange={onToggle} aria-label={`Include department ${d.DepartmentCode}`}
          className="h-4 w-4 shrink-0 accent-navy" />
        <button type="button" onClick={onExpand} aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} titles in ${d.DepartmentCode}`}
          className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <Chevron className="h-4 w-4 shrink-0 text-darkGrey/50" aria-hidden />
          <span className="font-mono text-sm font-semibold text-navy">{d.DepartmentCode}</span>
          <span className="truncate text-xs text-darkGrey/70">{d.DepartmentDescription}</span>
          <span className="ml-auto shrink-0 rounded-full bg-bg px-2 py-0.5 text-xs font-semibold text-darkGrey/70"
            title={`${row.titles.length} position ${row.titles.length === 1 ? 'title' : 'titles'}`}>
            {row.titles.length}
          </span>
        </button>
      </div>
      {expanded && (
        <ul className="space-y-1 border-t border-border px-3 py-2">
          {row.titles.length === 0 && <li className="text-xs text-darkGrey/60">No position titles.</li>}
          {row.titles.map((t) => {
            const added = pickedTitles.has(t.PositionTitle.toLowerCase());
            return (
              <li key={t.PositionTitleId} className="flex items-center gap-2 pl-6 text-sm">
                <span className={cn('flex-1 text-darkGrey', row.titleHits.has(t.PositionTitleId) && 'font-semibold text-navy')}>{t.PositionTitle}</span>
                <button type="button" disabled={added} onClick={() => onAddTitle(t.PositionTitle)}
                  aria-label={added ? `${t.PositionTitle} added` : `Add title ${t.PositionTitle}`}
                  title="Match this job title on its own, whatever the person's department"
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-border px-2 py-0.5 text-xs font-semibold text-navy hover:bg-navy/5 disabled:text-darkGrey/50">
                  {added ? 'Added' : <><Plus className="h-3 w-3" aria-hidden /> Title</>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
